import { useState, useCallback } from 'react';
import { ToastContext, type Toast, type ToastAction } from './ToastContext';
import { TOAST_DEFAULT_TIMEOUT_MS } from '../../utils/constants';

interface ToastProviderProps {
  children: React.ReactNode;
  timeoutMs?: number;
}

export function ToastProvider({
  children,
  timeoutMs = TOAST_DEFAULT_TIMEOUT_MS,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (
      message: string,
      type: Toast['type'] = 'success',
      action?: ToastAction
    ) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...(prev ?? []), { id, message, type, action }]);

      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, timeoutMs);
    },
    [timeoutMs]
  );

  const success = useCallback(
    (message: string, action?: ToastAction) =>
      showToast(message, 'success', action),
    [showToast]
  );

  const error = useCallback(
    (message: string, action?: ToastAction) =>
      showToast(message, 'error', action),
    [showToast]
  );

  const info = useCallback(
    (message: string, action?: ToastAction) =>
      showToast(message, 'info', action),
    [showToast]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-sm animate-slide-up ${
              toast.type === 'success'
                ? 'bg-green-600 text-white'
                : toast.type === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-brand-primary text-white'
            }`}
            role="alert"
          >
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onClick();
                  removeToast(toast.id);
                }}
                className="font-medium underline hover:no-underline whitespace-nowrap"
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => removeToast(toast.id)}
              className="text-white/80 hover:text-white"
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
